import React, { useState, useEffect } from 'react';
import { MusicPlayer, type MusicTrack } from './music-player';
import { AnnotationForm, type AnnotationFormData } from './annotation-form';
import { FixedFeedbackWidget } from '@/components/feedback';
import { FeedbackContextService } from '@/services/feedbackContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Clock, Music, ArrowRight, ArrowLeft, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Deterministic hash of a string to a number in [0, 1).
 * Uses djb2 hash algorithm for simplicity and consistency.
 */
function deterministicRandom(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash % 10000) / 10000;
}

export interface SessionSong {
  id: string;
  source: 'suno' | 'mureka' | 'sonauto' | 'udio' | 'human';
  title: string;
  audioUrl: string;
  duration: number;
}

export interface AnnotationSessionState {
  sessionId: string;
  participantId: string;
  currentSongIndex: number;
  totalSongs: number;
  completedAnnotations: number;
  estimatedTimeRemaining: number;
  currentSong?: SessionSong;
  isComplete: boolean;
}

interface AnnotationSessionProps {
  sessionState: AnnotationSessionState;
  sessionSongs: SessionSong[];
  onAnnotationSubmit: (annotation: AnnotationFormData, duration: number, listeningTime: number, songDuration?: number, listeningPercentage?: number) => Promise<void>;
  onSessionComplete: () => void;
  onError?: (error: string) => void;
  onPause?: () => void;
  allowNavigation?: boolean;
}

export function AnnotationSession({
  sessionState,
  sessionSongs,
  onAnnotationSubmit,
  onSessionComplete,
  onError,
  onPause,
  allowNavigation = false
}: AnnotationSessionProps) {
  const [currentSongIndex, setCurrentSongIndex] = useState(sessionState.currentSongIndex);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedSongs, setCompletedSongs] = useState<Set<number>>(
    new Set(Array.from({ length: sessionState.completedAnnotations }, (_, i) => i))
  );
  const [sessionStartTime] = useState(Date.now());
  const [songStartTime, setSongStartTime] = useState(Date.now());
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSongListeningTime, setCurrentSongListeningTime] = useState(0);
  const [currentSongDuration, setCurrentSongDuration] = useState(0);
  const [currentListeningPercentage, setCurrentListeningPercentage] = useState(0);
  const [isFirstSong, setIsFirstSong] = useState(sessionState.completedAnnotations === 0);

  // Sync with updated session state from parent (only when server state advances)
  useEffect(() => {
    // Only sync from server if it's ahead of local state (avoid regressing on race)
    if (sessionState.currentSongIndex > currentSongIndex) {
      setCurrentSongIndex(sessionState.currentSongIndex);
    }
    // Update completed songs based on session state
    const completedCount = sessionState.completedAnnotations;
    if (completedCount > 0) {
      setCompletedSongs(prev => {
        if (completedCount > prev.size) {
          return new Set(Array.from({ length: completedCount }, (_, i) => i));
        }
        return prev;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState.currentSongIndex, sessionState.completedAnnotations]);

  const currentSong = sessionSongs[currentSongIndex];
  const actualCompletedCount = Math.max(completedSongs.size, sessionState.completedAnnotations);
  const progress = (actualCompletedCount / sessionSongs.length) * 100;
  const isLastSong = currentSongIndex === sessionSongs.length - 1;
  const canGoNext = currentSongIndex < sessionSongs.length - 1;
  const canGoPrevious = currentSongIndex > 0;

  // Update song start time when song changes
  useEffect(() => {
    console.log('AnnotationSession: Song changed', {
      newIndex: currentSongIndex,
      songId: sessionSongs[currentSongIndex]?.id,
      resettingListeningTime: true
    });
    setSongStartTime(Date.now());
    setCurrentSongListeningTime(0); // Reset listening time for new song
    setCurrentSongDuration(0);
    setCurrentListeningPercentage(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSongIndex]);

  // Store session context for feedback
  useEffect(() => {
    if (sessionState.sessionId) {
      FeedbackContextService.setCurrentSessionId(sessionState.sessionId);
    }
    if (sessionState.participantId) {
      FeedbackContextService.setCurrentParticipantId(sessionState.participantId);
    }
  }, [sessionState.sessionId, sessionState.participantId]);

  // Convert SessionSong to MusicTrack for the player
  const musicTrack: MusicTrack = currentSong ? {
    id: currentSong.id,
    title: currentSong.title,
    audioUrl: currentSong.audioUrl,
    duration: currentSong.duration,
    source: currentSong.source
  } : {
    id: '',
    title: 'Loading...',
    audioUrl: '',
    source: 'suno'
  };

  const handleAnnotationSubmit = async (annotation: AnnotationFormData, duration: number, listeningTime: number) => {
    if (!currentSong || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onAnnotationSubmit(annotation, duration, listeningTime, currentSongDuration, currentListeningPercentage);
      
      // Mark current song as completed
      setCompletedSongs(prev => new Set([...prev, currentSongIndex]));

      // Move to next song or complete session
      if (isLastSong) {
        onSessionComplete();
      } else {
        setIsFirstSong(false); // No longer the first song after first annotation
        setCurrentSongIndex(prev => prev + 1);
      }

      // Smooth scroll to the Music Track card after successful submission
      setTimeout(() => {
        const musicTrackCard = document.getElementById('music-track-card');
        if (musicTrackCard) {
          musicTrackCard.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100); // Small delay to ensure the new content is rendered
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit annotation';
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreviousSong = () => {
    if (canGoPrevious) {
      setCurrentSongIndex(prev => prev - 1);
    }
  };

  const handleNextSong = () => {
    if (canGoNext) {
      setCurrentSongIndex(prev => prev + 1);
    }
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const estimatedTimeRemaining = () => {
    const remainingSongs = sessionSongs.length - actualCompletedCount;
    const avgTimePerSong = 3; // Estimate 3 minutes per song
    return remainingSongs * avgTimePerSong;
  };

  if (!currentSong) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Loading session...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 md:space-y-6 px-4 md:px-0">
      {/* Session Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
              <Music className="h-5 w-5" />
              Annotation Session
            </CardTitle>
            <div className="flex items-center gap-2">
              {onPause && (
                <Button variant="outline" onClick={onPause} className="gap-2 text-sm">
                  <Pause className="h-4 w-4" />
                  <span className="hidden sm:inline">Pause Session</span>
                  <span className="sm:hidden">Pause</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm">
              <span>Progress: {actualCompletedCount} of {sessionSongs.length} songs</span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3 w-3" />
                ~{formatTime(estimatedTimeRemaining())} remaining
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Current Song Info */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium">Song {currentSongIndex + 1} of {sessionSongs.length}</p>
              <p className="text-sm text-muted-foreground">
                Ready for annotation
              </p>
            </div>
            <div className="flex items-center gap-2">
              {completedSongs.has(currentSongIndex) && (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
              {isPlaying && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Playing
                </div>
              )}
            </div>
          </div>

          {/* Navigation Controls */}
          {allowNavigation && (
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={handlePreviousSong}
                disabled={!canGoPrevious}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous Song
              </Button>
              <Button
                variant="outline"
                onClick={handleNextSong}
                disabled={!canGoNext}
                className="gap-2"
              >
                Next Song
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Music Player */}
      <MusicPlayer
        track={musicTrack}
        onPlayStateChange={setIsPlaying}
        onLoadError={(error) => onError?.(error)}
        onListeningTimeUpdate={(listeningTime, songDuration, percentage) => {
          console.log('AnnotationSession: Listening time update', {
            songIndex: currentSongIndex,
            songId: currentSong?.id,
            listeningTime: Math.round(listeningTime),
            songDuration: Math.round(songDuration),
            percentage: Math.round(percentage * 100) / 100
          });
          setCurrentSongListeningTime(listeningTime);
          setCurrentSongDuration(songDuration);
          setCurrentListeningPercentage(percentage);
        }}
        autoPlay={!isFirstSong}
        maxPlayDuration={currentSong?.source !== 'human' ? 29 : undefined}
        playStartOffset={currentSong?.source !== 'human' ? (() => {
          const snippetDuration = 29;
          if (currentSong.duration > 0) {
            // Known duration: pick within the first 2/3 of the song
            const songDurationSec = currentSong.duration / 1000;
            const maxStart = Math.max(0, (songDurationSec * 2 / 3) - snippetDuration);
            return maxStart > 0 ? Math.floor(deterministicRandom(currentSong.id) * maxStart) : 0;
          }
          // Unknown duration: pick a random offset within 0–31s (so snippet ends by ~60s)
          const maxStart = 60 - snippetDuration; // 31
          return Math.floor(deterministicRandom(currentSong.id) * maxStart);
        })() : undefined}
        className="sticky top-4 z-10"
      />

      {/* Annotation Form */}
      <AnnotationForm
        onSubmit={handleAnnotationSubmit}
        onError={onError}
        isLoading={isSubmitting}
        listeningTime={currentSongListeningTime}
        resetTrigger={currentSongIndex} // Use resetTrigger instead of key to preserve form state during song playback
      />

      {/* Session Stats */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-primary">{actualCompletedCount}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{sessionSongs.length - actualCompletedCount}</p>
              <p className="text-sm text-muted-foreground">Remaining</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{Math.round(progress)}%</p>
              <p className="text-sm text-muted-foreground">Progress</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{formatTime(estimatedTimeRemaining())}</p>
              <p className="text-sm text-muted-foreground">Est. Time</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Feedback Button */}
      <FixedFeedbackWidget context="annotation" />
    </div>
  );
}